import { Component, OnInit, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, Platform, LoadingController, MenuController, AlertController } from '@ionic/angular/lazy';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Network } from '@capacitor/network';
import { GlobalVariableService } from 'src/app/services/globalvariable.service';
import { ObserverService } from 'src/app/services/observer.service';
import { StorageService } from 'src/app/services/storage.service';
import { PusherService } from 'src/app/services/pusher.service';
import { CartService } from 'src/app/services/cart.service';
import { ProductService } from 'src/app/services/product.service';
import { PersonService } from 'src/app/services/person.service';
import { EventService } from 'src/app/services/event.service';
import { RegisterService } from 'src/app/services/register.service';
import { UserService } from 'src/app/services/user.service';
import { PricingTierService } from 'src/app/services/pricing-tier.service';
import { TaxZoneService } from 'src/app/services/tax-zone.service';
import { SupportedCurrencyService } from 'src/app/services/supported-currency.service';
import { PendingOrderService } from 'src/app/services/pending-order.service';
import { CountryService } from 'src/app/services/country.service';
import { CourierService } from 'src/app/services/courier.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { CustomPaymentServiceService } from 'src/app/services/custom-payment-service.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import * as Sentry from "@sentry/capacitor";
import { Console } from 'console';
import { StripeTerminalReaderService } from 'src/app/services/stripe-terminal-reader.service';
import { SmartpayReaderService } from 'src/app/services/smartpay-reader.service';
import { ParkedCartLegacyService } from 'src/app/services/parked-cart-legacy.service';
import { MeasurementUnitService } from 'src/app/services/measurement-unit.service';
import { PermissionService } from 'src/app/services/permission.service';
import { UserPINChangePage } from 'src/app/modals/user-pin-change/user-pin-change.page';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {

    loading_data = false;
    online = false;
    pending_order_count = 0;
    connected_account = null;
    user = null;
    version = null;
    pusher_channel = null;
    register = null;
    spinner = null;
    new_version_available_alert = null;
    new_version_available_alerted = false;
    update_available_alerted = false;
    update_required_alerted = false;

    constructor(
        private platform: Platform,
        private loadingController: LoadingController,
        private router: Router,
        private menu: MenuController,
        public globalvar: GlobalVariableService,
        public modalController: ModalController,
        private observerService: ObserverService,
        private storageService: StorageService,
        public alertController: AlertController,
        private pusherService: PusherService,
        private cartService: CartService,
        private productService: ProductService,
        private personService: PersonService,
        private eventService: EventService,
        private stripeTerminalReaderService: StripeTerminalReaderService,
        private smartpayReaderService: SmartpayReaderService,
        private registerService: RegisterService,
        private userService: UserService,
        private pricingTierService: PricingTierService,
        private taxZoneService: TaxZoneService,
        private supportedCurrencyService: SupportedCurrencyService,
        private pendingOrderService: PendingOrderService,
        private countryService: CountryService,
        private courierService: CourierService,
        private connectedAccountService: ConnectedAccountService,
        private apiService: APIService,
        private organisationService: OrganisationService,
        private customPaymentServiceService: CustomPaymentServiceService,
        private authService: AuthService,
        private location: Location,
        private parkedCartLegacyService: ParkedCartLegacyService,
        private measurementUnitService: MeasurementUnitService,
        private ngZone: NgZone,
        public permissionService: PermissionService
    ) {
        this.initializeApp();
    }

    initializeApp() {
        this.platform.ready().then(() => {
            // on device (not browser)
            if(Capacitor.isNativePlatform()) {
                SplashScreen.hide();

                if(Capacitor.getPlatform() === 'ios') {
                    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => { });
                }
            }
        })
        .catch((error) => {
            console.error(error);
        });
    }

    checkVersion = () => {
        // NOTE: don't check while data is loading, avoids the alert appearing over the loading spinner
        if(!this.new_version_available_alerted && !this.loading_data) {
            fetch('https://pos-origin.airsquare.com/version.json', {cache: "no-store"})
            .then(res => res.json())
            .then(json => {
                if(json.version != this.globalvar.version) {
                    if(this.new_version_available_alert) {
                        this.new_version_available_alert.dismiss();
                    }
                    this.presentNewVersionAvailableAlert('New version available', "Woohoo! New version of the POS is available, please refresh this page.");
                }
            })
            .catch((error) => {
                console.error(error);
            });
        }
    }

    checkNativeVersion = () => {
        // NOTE: don't check while data is loading, avoids the alert appearing over the loading spinner
        if(!this.update_required_alerted && !this.loading_data) {
            fetch('https://version.airsquare.com/pos.json', {cache: "no-store"})
            .then(res => res.json())
            .then(json => {
                // NOTE: only acts when this build is behind, a missing, stale or invalid version is ignored so the POS keeps working
                const platform_versions = json[Capacitor.getPlatform()];
                const current_version = Number(this.globalvar.version);
                if(platform_versions) {
                    if(Number.isInteger(platform_versions.minimum_version) && current_version < platform_versions.minimum_version) {
                        // NOTE: don't block mid-sale, a later check blocks once the cart is empty
                        this.storageService.get("cart").then((cart) => {
                            if(!this.update_required_alerted && !(cart && cart['items'] && cart['items'].length)) {
                                this.presentUpdateRequiredAlert();
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    }
                    else if(!this.update_available_alerted && Number.isInteger(platform_versions.latest_version) && current_version < platform_versions.latest_version) {
                        this.presentUpdateAvailableAlert();
                    }
                }
            })
            // NOTE: offline or version.airsquare.com unreachable, carry on as normal
            .catch(() => { });
        }
    }

    openAppStore() {
        if(Capacitor.getPlatform() === 'ios') {
            Browser.open({ url: 'https://apps.apple.com/app/id1545263191' });
        }
        else {
            Browser.open({ url: 'https://play.google.com/store/apps/details?id=com.airsquare.airsquarepos' });
        }
    }

    private handleOAuthCallback(url: string) {
        if (!url.startsWith('com.airsquare.airsquarepos://oauth-callback')) return;
        Browser.close().catch(() => { });
        this.ngZone.run(() => {
            this.router.navigateByUrl('/oauth-callback' + url.substring(url.indexOf('?')),{ replaceUrl: true });
        });
    }

    async ngOnInit() {

        if(this.location.path() != '/reset') {

            this.platform.ready().then(async () => {

                let network_status = await Network.getStatus();
                this.online = network_status.connected;

                Network.addListener("networkStatusChange", (network_status) => {
                    if(network_status.connected === this.online) {
                        return; // No change, ignore noisy Android events
                    }
                    this.observerService.publish("network_status_changed", network_status);
                    this.online = network_status.connected;
                    if(this.online) {
                        setTimeout(() => {
                            this.storageService.get("connected_account_id").then((connected_account_id) => {
                                if (connected_account_id) {
                                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                                        this.connected_account = connected_account;
                                        this.loadData().then().catch(() => { });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        }, 100);
                    }
                });

                if(Capacitor.isNativePlatform()) {
                    // Cold start
                    App.getLaunchUrl().then(launch => {
                        if(launch?.url) this.handleOAuthCallback(launch.url);
                    });
                    // Warm start
                    App.addListener('appUrlOpen', ({ url }) => {
                        if(url) this.handleOAuthCallback(url);
                    });                    
                    App.addListener("appStateChange", ({ isActive }) => {
                        if (isActive) {
                            if(this.online) {
                                this.storageService.get("connected_account_id").then((connected_account_id) => {
                                    if (connected_account_id) {
                                        this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                                            this.connected_account = connected_account;
                                            this.loadData().then().catch(() => { });
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    }
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                            }
                        }
                    });
                    // check for an app update once data has loaded (on start and resume), rather than on start
                    this.observerService.subscribe("load_data_completed|app_component", () => {
                        this.checkNativeVersion();
                    });
                    // check for an app update every 5 mins
                    setInterval(this.checkNativeVersion, 300000);
                }
                else {
                    // check for new version once data has loaded, rather than on start
                    this.observerService.subscribe("load_data_completed|app_component", () => {
                        this.checkVersion();
                    });
                    // check for new version every 5 mins
                    setInterval(this.checkVersion, 300000);
                }

            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.set('new_order',true);

            this.storageService.get("printer_paper_sizes").then((printer_paper_sizes) => {
                if(printer_paper_sizes === null) {
                    if(Capacitor.isNativePlatform()) {
                        this.storageService.set('printer_paper_sizes', ['thermal']);
                    }
                    else {
                        this.storageService.set('printer_paper_sizes', ['a4']);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("close_variant_modal").then((close_variant_modal) => {
                if(close_variant_modal === null) {
                    this.storageService.set('close_variant_modal', true);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("close_ticket_type_modal").then((close_ticket_type_modal) => {
                if(close_ticket_type_modal === null) {
                    this.storageService.set('close_ticket_type_modal', true);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("default_sell_screen_mode").then((default_sell_screen_mode) => {
                if(default_sell_screen_mode === null) {
                    this.storageService.set('default_sell_screen_mode', 'products');
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("sell_screen_listing_layout").then((sell_screen_listing_layout) => {
                if(sell_screen_listing_layout === null) {
                    this.storageService.set('sell_screen_listing_layout', 'grid');
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("show_sku").then((show_sku) => {
                if(show_sku === null) {
                    this.storageService.set('show_sku', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("checkout_auto_place_order_when_fully_paid").then((checkout_auto_place_order_when_fully_paid) => {
                if(checkout_auto_place_order_when_fully_paid === null) {
                    this.storageService.set('checkout_auto_place_order_when_fully_paid', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("desktop_print_client_enabled").then((desktop_print_client_enabled) => {
                if(desktop_print_client_enabled === null) {
                    this.storageService.set('desktop_print_client_enabled', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                if(alert_if_overselling_item === null) {
                    this.storageService.set('alert_if_overselling_item', true);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("auto_hide_price_breakdown").then((auto_hide_price_breakdown) => {
                if(auto_hide_price_breakdown === null) {
                    this.storageService.set('auto_hide_price_breakdown', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("show_profit_on_sell_screen_cart").then((show_profit_on_sell_screen_cart) => {
                if(show_profit_on_sell_screen_cart === null) {
                    this.storageService.set('show_profit_on_sell_screen_cart', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });            

            this.storageService.get("show_margin_on_sell_screen_cart").then((show_margin_on_sell_screen_cart) => {
                if(show_margin_on_sell_screen_cart === null) {
                    this.storageService.set('show_margin_on_sell_screen_cart', false);
                }
            })
            .catch((error) => {
                console.error(error);
            });                     

            // ensure cart is cleared on start
            this.storageService.get("cart").then((cart) => {
                if (cart) {
                    this.storageService.remove('cart');
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.version = this.globalvar.version;

            this.storageService.get("connected_account_id").then((connected_account_id) => {
                if (connected_account_id) {
                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                        if(connected_account) {
                            this.connected_account = connected_account;
                            Sentry.setContext("site", {
                                name: connected_account['site_name'],
                                server_name: connected_account['server_name'],
                                organisation_title: connected_account['organisation_title']
                            });
                            this.loadData().then().catch(() => { });
                        }
                        else {
                            // for some reason the connected account is no longer available in the db, disconnect and boot
                            this.authService.disconnectAccount(connected_account_id).then(() => {
                                this.router.navigate(['switch-account'], { replaceUrl: true });
                            });
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.storageService.get("register_id").then((register_id) => {
                if (register_id) {
                    this.registerService.getRegister(register_id).then((register) => {
                        if (register) {
                            this.register = register;
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            });

            this.observerService.subscribe("connected_account_id_changed|app_component", (connected_account_id) => {
                this.storageService.get('connected_account_id').then((connected_account_id) => {
                    if (connected_account_id) {
                        this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                            if (connected_account) {
                                this.connected_account = connected_account;
                                Sentry.setContext("site", {
                                    name: connected_account['site_name'],
                                    server_name: connected_account['server_name'],
                                    organisation_title: connected_account['organisation_title']
                                });
                                this.loadData().then().catch(() => { });
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            });

            this.observerService.subscribe("connected_account_access_token_changed|app_component", () => {
                this.initializePusher();
            });

            this.observerService.subscribe("logged_out|app_component", (data) => {
                this.pusherService.disconnect();
            });

            this.observerService.subscribe("register_id_changed|app_component", (register_id) => {
                this.registerService.getRegister(register_id).then((register) => {
                    if (register) {
                        this.register = register;
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            });

            this.observerService.subscribe("register_updated|app_component", (register) => {
                if (register) {
                    if (register.id === this.register.id) {
                        this.register = register;
                    }
                }
            });

            this.observerService.subscribe("user_updated|app_component", (user) => {
                if(user) {
                    if(user.id === this.user.id) {
                        this.user = user;
                        Sentry.setContext("user", {
                            first_name: user['first_name'],
                            last_name: user['last_name'],
                            username: user['username'],
                            email_address: user['email_address']
                        });
                    }
                }
            });

            this.observerService.subscribe("user_deleted|app_component", (user) => {
                if(user) {
                    if(user.id === this.user.id) {
                        this.router.navigate(['disconnect-account'], { replaceUrl: true });
                    }
                }
            });

            this.observerService.subscribe('registers_changed|app_component', (users) => {
                this.storageService.get("register_id").then((register_id) => {
                    if(register_id) {
                        this.registerService.getRegister(register_id).then((register) => {
                            if(register) {
                                this.register = register;
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            });

            this.observerService.subscribe('users_changed|app_component', (users) => {
                this.connectedAccountService.getConnectedAccounts().then((connected_accounts: any) => {
                    if(connected_accounts) {
                        for(let i = 0; i < connected_accounts.length; i++) {
                            let connected_account = connected_accounts[i];
                            let user_exists = false;
                            for(let j = 0; j < users.length; j++) {
                                if(users[j].id === connected_account.user_id) {
                                    user_exists = true;
                                    break;
                                }
                            }
                            if(!user_exists) {
                                this.connectedAccountService.deleteConnectedAccount(connected_account.id);
                            }
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            });

        }
    }

    loadData() {
        const promise = new Promise<void>(async (resolve, reject) => {
            if (!this.loading_data) {
                this.loading_data = true;
                this.presentSpinner("Initialising").then(async () => {
                    if(this.connected_account) {
                        await this.storageService.getMultiple(['registers_last_updated_date','supported_currencies_last_updated_date','tax_zones_last_updated_date','pricing_tiers_last_updated_date','product_categories_last_updated_date','event_categories_last_updated_date','person_email_lists_last_updated_date','countries_last_updated_date','couriers_last_updated_date','products_last_updated_date','events_last_updated_date','people_last_updated_date','organisations_last_updated_date','custom_payment_services_last_updated_date','measurement_units_last_updated_date','users_last_updated_date']).then((compiled_data_params) => {
                            this.spinner.message = "Retrieving data";
                            this.apiService.getCompiledData(compiled_data_params).then((compiled_data: {}) => {
                                if(compiled_data['settings']) {

                                    let gift_voucher_tax_type = null;
                                    let order_shipping_tax_type = null;
                                    let order_credit_card_surcharge_rate = null;
                                    let order_payment_surcharge_tax_type = null;
                                    let organisation_use_contract_price_even_if_more_expensive = null;
                                    let order_pos_shipping_address_require_first_name = null;
                                    let order_pos_shipping_address_require_last_name = null;
                                    let order_pos_shipping_address_require_line_1 = null;
                                    let order_pos_shipping_address_require_locality = null;
                                    let order_pos_shipping_address_require_subdivision = null;
                                    let order_pos_shipping_address_require_postal_code = null;
                                    let order_pos_shipping_address_require_mobile_phone_number = null;
                                    let google_maps_public_api_key = null;
                                    let quote_default_expiry_days = null;

                                    for (let i = 0; i < compiled_data['settings'].length; i++) {

                                        let setting = compiled_data['settings'][i];

                                        if (setting['key'] == 'gift_voucher_tax_type') {
                                            gift_voucher_tax_type = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_shipping_tax_type') {
                                            order_shipping_tax_type = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_credit_card_surcharge_rate'){
                                            order_credit_card_surcharge_rate = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_payment_surcharge_tax_type') {
                                            order_payment_surcharge_tax_type = setting['value'];
                                        }
                                        else if (setting['key'] == 'organisation_use_contract_price_even_if_more_expensive') {
                                            organisation_use_contract_price_even_if_more_expensive = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_first_name') {
                                            order_pos_shipping_address_require_first_name = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_last_name') {
                                            order_pos_shipping_address_require_last_name = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_line_1') {
                                            order_pos_shipping_address_require_line_1 = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_locality') {
                                            order_pos_shipping_address_require_locality = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_subdivision') {
                                            order_pos_shipping_address_require_subdivision = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_postal_code') {
                                            order_pos_shipping_address_require_postal_code = setting['value'];
                                        }
                                        else if (setting['key'] == 'order_pos_shipping_address_require_mobile_phone_number') {
                                            order_pos_shipping_address_require_mobile_phone_number = setting['value'];
                                        }
                                        else if (setting['key'] == 'google_maps_public_api_key') {
                                            google_maps_public_api_key = setting['value'];
                                        }
                                        else if (setting['key'] == 'quote_default_expiry_days') {
                                            quote_default_expiry_days = setting['value'];
                                        }

                                    }

                                    if(order_payment_surcharge_tax_type !== null) {
                                        this.storageService.set('order_payment_surcharge_tax_type', order_payment_surcharge_tax_type).then(() => {
                                            if(order_credit_card_surcharge_rate !== null) {
                                                this.storageService.set('order_credit_card_surcharge_rate', order_credit_card_surcharge_rate).then(() => {
                                                    if(order_shipping_tax_type !== null) {
                                                        this.storageService.set('order_shipping_tax_type', order_shipping_tax_type).then(() => {
                                                            if(gift_voucher_tax_type !== null) {
                                                                this.storageService.set('gift_voucher_tax_type', gift_voucher_tax_type).then(() => {
                                                                    if(organisation_use_contract_price_even_if_more_expensive !== null) {
                                                                        this.storageService.set('organisation_use_contract_price_even_if_more_expensive', organisation_use_contract_price_even_if_more_expensive).then(() => {
                                                                            if(order_pos_shipping_address_require_first_name !== null) {
                                                                                this.storageService.set('order_pos_shipping_address_require_first_name', order_pos_shipping_address_require_first_name).then(() => {
                                                                                    if(order_pos_shipping_address_require_last_name !== null) {
                                                                                        this.storageService.set('order_pos_shipping_address_require_last_name', order_pos_shipping_address_require_last_name).then(() => {
                                                                                            if(order_pos_shipping_address_require_line_1 !== null) {
                                                                                                this.storageService.set('order_pos_shipping_address_require_line_1', order_pos_shipping_address_require_line_1).then(() => {
                                                                                                    if(order_pos_shipping_address_require_locality !== null) {
                                                                                                        this.storageService.set('order_pos_shipping_address_require_locality', order_pos_shipping_address_require_locality).then(() => {
                                                                                                            if(order_pos_shipping_address_require_subdivision !== null) {
                                                                                                                this.storageService.set('order_pos_shipping_address_require_subdivision', order_pos_shipping_address_require_subdivision).then(() => {
                                                                                                                    if(order_pos_shipping_address_require_postal_code !== null) {
                                                                                                                        this.storageService.set('order_pos_shipping_address_require_postal_code', order_pos_shipping_address_require_postal_code).then(() => {
                                                                                                                            if(order_pos_shipping_address_require_mobile_phone_number !== null) {
                                                                                                                                this.storageService.set('order_pos_shipping_address_require_mobile_phone_number', order_pos_shipping_address_require_mobile_phone_number).then(() => {
                                                                                                                                    if(google_maps_public_api_key !== null) {
                                                                                                                                        this.storageService.set('google_maps_public_api_key', google_maps_public_api_key).then(() => {
                                                                                                                                            if(quote_default_expiry_days !== null) {
                                                                                                                                                this.storageService.set('quote_default_expiry_days', quote_default_expiry_days).then(() => {
                                                                                                                                                    this.spinner.message = "Loading users";
                                                                                                                                                    this.userService.bulkAddUsers(compiled_data['users']).then(() => {
                                                                                                                                                        this.userService.getUser(this.connected_account.user_id).then((user) => {
                                                                                                                                                            if(user) {
                                                                                                                                                                this.user = user;
                                                                                                                                                                Sentry.setContext("user", {
                                                                                                                                                                    first_name: user['first_name'],
                                                                                                                                                                    last_name: user['last_name'],
                                                                                                                                                                    username: user['username'],
                                                                                                                                                                    email_address: user['email_address']
                                                                                                                                                                });
                                                                                                                                                                this.spinner.message = "Loading custom payment services";
                                                                                                                                                                this.customPaymentServiceService.bulkAddCustomPaymentServices(compiled_data['custom_payment_services']).then(() => {
                                                                                                                                                                    this.spinner.message = "Loading registers";
                                                                                                                                                                    this.registerService.bulkAddRegisters(compiled_data['registers']).then(() => {
                                                                                                                                                                        this.storageService.get("register_id").then((register_id) => {
                                                                                                                                                                            this.registerService.getRegisters().then((registers:[]) => {
                                                                                                                                                                                if (registers) {
                                                                                                                                                                                    let default_register_id = null;
                                                                                                                                                                                    let register_id_valid = false;
                                                                                                                                                                                    for (let i = 0; i < registers.length; i++) {
                                                                                                                                                                                        default_register_id = registers[i]["id"];
                                                                                                                                                                                        break;
                                                                                                                                                                                    }
                                                                                                                                                                                    if (register_id) {
                                                                                                                                                                                        for (let i = 0; i < registers.length; i++) {
                                                                                                                                                                                            if (registers[i]["id"] === register_id) {
                                                                                                                                                                                                register_id_valid = true;
                                                                                                                                                                                                break;
                                                                                                                                                                                            }
                                                                                                                                                                                        }
                                                                                                                                                                                    }
                                                                                                                                                                                    if (!register_id_valid) {
                                                                                                                                                                                        this.storageService.set("register_id", default_register_id).then(() => {
                                                                                                                                                                                            this.observerService.publish("register_id_changed", default_register_id);
                                                                                                                                                                                        });
                                                                                                                                                                                    }
                                                                                                                                                                                    this.spinner.message = "Loading supported currencies";
                                                                                                                                                                                    this.supportedCurrencyService.bulkAddSupportedCurrencies(compiled_data['supported_currencies']).then(() => {
                                                                                                                                                                                        this.cartService.validateCurrency().then(() => {
                                                                                                                                                                                            this.spinner.message = "Loading tax zones";
                                                                                                                                                                                            this.taxZoneService.bulkAddTaxZones(compiled_data['tax_zones']).then(() => {
                                                                                                                                                                                                this.cartService.validateTaxZone().then(() => {
                                                                                                                                                                                                    this.spinner.message = "Loading pricing tiers";
                                                                                                                                                                                                    this.pricingTierService.bulkAddPricingTiers(compiled_data['pricing_tiers']).then(() => {
                                                                                                                                                                                                        this.cartService.validatePricingTier().then(() => {
                                                                                                                                                                                                            this.spinner.message = "Loading product categories";
                                                                                                                                                                                                            this.productService.bulkAddProductCategories(compiled_data['product_categories']).then(() => {
                                                                                                                                                                                                                this.spinner.message = "Loading products";
                                                                                                                                                                                                                this.productService.bulkAddProducts(compiled_data['products']).then(() => {
                                                                                                                                                                                                                    this.spinner.message = "Loading event categories";
                                                                                                                                                                                                                    this.eventService.bulkAddEventCategories(compiled_data['event_categories']).then(() => {
                                                                                                                                                                                                                        this.spinner.message = "Loading events";
                                                                                                                                                                                                                        this.eventService.bulkAddEvents(compiled_data['events']).then(() => {
                                                                                                                                                                                                                            this.cartService.validateItems().then(() => {
                                                                                                                                                                                                                                this.spinner.message = "Loading people";
                                                                                                                                                                                                                                this.personService.bulkAddPeople(compiled_data['people']).then(() => {
                                                                                                                                                                                                                                    this.cartService.validatePerson().then(() => {
                                                                                                                                                                                                                                        this.spinner.message = "Loading countries";
                                                                                                                                                                                                                                        this.countryService.bulkAddCountries(compiled_data['countries']).then(() => {
                                                                                                                                                                                                                                            this.spinner.message = "Loading couriers";
                                                                                                                                                                                                                                            this.courierService.bulkAddCouriers(compiled_data['couriers']).then(() => {
                                                                                                                                                                                                                                                this.spinner.message = "Loading organisations";
                                                                                                                                                                                                                                                this.organisationService.bulkAddOrganisations(compiled_data['organisations']).then(() => {
                                                                                                                                                                                                                                                    this.cartService.validateOrganisation().then(() => {
                                                                                                                                                                                                                                                        this.spinner.message = "Loading measurement units";
                                                                                                                                                                                                                                                            this.measurementUnitService.bulkAddMeasurementUnits(compiled_data['measurement_units']).then(() => {
                                                                                                                                                                                                                                                            this.cartService.get().then((cart) => {
                                                                                                                                                                                                                                                                this.cartService.resetItems(cart, !cart['parked_cart_id'] && !cart['quote_id']).then((cart) => {
                                                                                                                                                                                                                                                                    this.cartService.set(cart).then(() => {
                                                                                                                                                                                                                                                                        // NOTE: sends in the background, so the till isn't held up while they go
                                                                                                                                                                                                                                                                        this.pendingOrderService.processPendingOrders().then(() => {}).catch((error) => {
                                                                                                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                                                                                                        });
                                                                                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                                                                                                        this.observerService.publish("load_data_completed", {});
                                                                                                                                                                                                                                                                        this.initializePusher();
                                                                                                                                                                                                                                                                        resolve();
                                                                                                                                                                                                                                                                    })
                                                                                                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                                                                                    });
                                                                                                                                                                                                                                                                })
                                                                                                                                                                                                                                                                .catch((error) => {
                                                                                                                                                                                                                                                                    console.error(error);
                                                                                                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                                                                                                });
                                                                                                                                                                                                                                                            })
                                                                                                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                                                                                                console.error(error);
                                                                                                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                                                                                            });
                                                                                                                                                                                                                                                        })
                                                                                                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                                                                                                            reject();
                                                                                                                                                                                                                                                        });
                                                                                                                                                                                                                                                    })
                                                                                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                                                                                        reject();
                                                                                                                                                                                                                                                    });
                                                                                                                                                                                                                                                })
                                                                                                                                                                                                                                                .catch((error) => {
                                                                                                                                                                                                                                                    console.error(error);
                                                                                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                                                                                                    reject();
                                                                                                                                                                                                                                                });
                                                                                                                                                                                                                                            })
                                                                                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                                                                                console.error(error);
                                                                                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                                                                                                reject();
                                                                                                                                                                                                                                            });
                                                                                                                                                                                                                                        })
                                                                                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                                                                                            reject();
                                                                                                                                                                                                                                        });
                                                                                                                                                                                                                                    })
                                                                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                                                                        reject();
                                                                                                                                                                                                                                    });
                                                                                                                                                                                                                                })
                                                                                                                                                                                                                                .catch((error) => {
                                                                                                                                                                                                                                    console.error(error);
                                                                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                                                                                    reject();
                                                                                                                                                                                                                                });
                                                                                                                                                                                                                            })
                                                                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                                                                console.error(error);
                                                                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                                                                                reject();
                                                                                                                                                                                                                            });
                                                                                                                                                                                                                        })
                                                                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                                                                            reject();
                                                                                                                                                                                                                        });
                                                                                                                                                                                                                    })
                                                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                                                        reject();
                                                                                                                                                                                                                    });
                                                                                                                                                                                                                })
                                                                                                                                                                                                                .catch((error) => {
                                                                                                                                                                                                                    console.error(error);
                                                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                                                                    reject();
                                                                                                                                                                                                                });
                                                                                                                                                                                                            })
                                                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                                                console.error(error);
                                                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                                                                reject();
                                                                                                                                                                                                            });
                                                                                                                                                                                                        })
                                                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                                                            reject();
                                                                                                                                                                                                        });
                                                                                                                                                                                                    })
                                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                                        reject();
                                                                                                                                                                                                    });
                                                                                                                                                                                                })
                                                                                                                                                                                                .catch((error) => {
                                                                                                                                                                                                    console.error(error);
                                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                                                    reject();
                                                                                                                                                                                                });
                                                                                                                                                                                            })
                                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                                console.error(error);
                                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                                                reject();
                                                                                                                                                                                            });
                                                                                                                                                                                        })
                                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                                            console.error(error);
                                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                                            reject();
                                                                                                                                                                                        });
                                                                                                                                                                                    })
                                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                                        console.error(error);
                                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                                        reject();
                                                                                                                                                                                    });
                                                                                                                                                                                }
                                                                                                                                                                                else {
                                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                                    reject();
                                                                                                                                                                                }
                                                                                                                                                                            })
                                                                                                                                                                            .catch((error) => {
                                                                                                                                                                                console.error(error);
                                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                                reject();
                                                                                                                                                                            });
                                                                                                                                                                        })
                                                                                                                                                                        .catch((error) => {
                                                                                                                                                                            console.error(error);
                                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                                            this.loading_data = false;
                                                                                                                                                                            reject();
                                                                                                                                                                        });
                                                                                                                                                                    })
                                                                                                                                                                    .catch((error) => {
                                                                                                                                                                        console.error(error);
                                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                                        this.loading_data = false;
                                                                                                                                                                        reject();
                                                                                                                                                                    });
                                                                                                                                                                })
                                                                                                                                                                .catch((error) => {
                                                                                                                                                                    console.error(error);
                                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                                    this.loading_data = false;
                                                                                                                                                                    reject();
                                                                                                                                                                });
                                                                                                                                                            }
                                                                                                                                                            else {
                                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                                this.loading_data = false;
                                                                                                                                                                reject();
                                                                                                                                                            }
                                                                                                                                                        })
                                                                                                                                                        .catch((error) => {
                                                                                                                                                            console.error(error);
                                                                                                                                                            this.spinner.dismiss();
                                                                                                                                                            this.loading_data = false;
                                                                                                                                                            reject();
                                                                                                                                                        });
                                                                                                                                                    })
                                                                                                                                                    .catch((error) => {
                                                                                                                                                        console.error(error);
                                                                                                                                                        this.spinner.dismiss();
                                                                                                                                                        this.loading_data = false;
                                                                                                                                                        reject();
                                                                                                                                                    });
                                                                                                                                                })
                                                                                                                                                .catch((error) => {
                                                                                                                                                    console.error(error);
                                                                                                                                                    this.spinner.dismiss();
                                                                                                                                                    this.loading_data = false;
                                                                                                                                                    reject();
                                                                                                                                                });
                                                                                                                                            }
                                                                                                                                            else {
                                                                                                                                                this.spinner.dismiss();
                                                                                                                                                this.loading_data = false;
                                                                                                                                                reject();
                                                                                                                                            }
                                                                                                                                        })
                                                                                                                                        .catch((error) => {
                                                                                                                                            console.error(error);
                                                                                                                                            this.spinner.dismiss();
                                                                                                                                            this.loading_data = false;
                                                                                                                                            reject();
                                                                                                                                        }); 
                                                                                                                                    }
                                                                                                                                    else {
                                                                                                                                        this.spinner.dismiss();
                                                                                                                                        this.loading_data = false;
                                                                                                                                        reject();
                                                                                                                                    }                                                                                                                                       
                                                                                                                                })
                                                                                                                                .catch((error) => {
                                                                                                                                    console.error(error);
                                                                                                                                    this.spinner.dismiss();
                                                                                                                                    this.loading_data = false;
                                                                                                                                    reject();
                                                                                                                                });
                                                                                                                            }
                                                                                                                            else {
                                                                                                                                this.spinner.dismiss();
                                                                                                                                this.loading_data = false;
                                                                                                                                reject();
                                                                                                                            }
                                                                                                                        })
                                                                                                                        .catch((error) => {
                                                                                                                            console.error(error);
                                                                                                                            this.spinner.dismiss();
                                                                                                                            this.loading_data = false;
                                                                                                                            reject();
                                                                                                                        });
                                                                                                                    }
                                                                                                                    else {
                                                                                                                        this.spinner.dismiss();
                                                                                                                        this.loading_data = false;
                                                                                                                        reject();
                                                                                                                    }
                                                                                                                })
                                                                                                                .catch((error) => {
                                                                                                                    console.error(error);
                                                                                                                    this.spinner.dismiss();
                                                                                                                    this.loading_data = false;
                                                                                                                    reject();
                                                                                                                });
                                                                                                            }
                                                                                                            else {
                                                                                                                this.spinner.dismiss();
                                                                                                                this.loading_data = false;
                                                                                                                reject();
                                                                                                            }
                                                                                                        })
                                                                                                        .catch((error) => {
                                                                                                            console.error(error);
                                                                                                            this.spinner.dismiss();
                                                                                                            this.loading_data = false;
                                                                                                            reject();
                                                                                                        });
                                                                                                    }
                                                                                                    else {
                                                                                                        this.spinner.dismiss();
                                                                                                        this.loading_data = false;
                                                                                                        reject();
                                                                                                    }
                                                                                                })
                                                                                                .catch((error) => {
                                                                                                    console.error(error);
                                                                                                    this.spinner.dismiss();
                                                                                                    this.loading_data = false;
                                                                                                    reject();
                                                                                                });
                                                                                            }
                                                                                            else {
                                                                                                this.spinner.dismiss();
                                                                                                this.loading_data = false;
                                                                                                reject();
                                                                                            }
                                                                                        })
                                                                                        .catch((error) => {
                                                                                            console.error(error);
                                                                                            this.spinner.dismiss();
                                                                                            this.loading_data = false;
                                                                                            reject();
                                                                                        });
                                                                                    }
                                                                                    else {
                                                                                        this.spinner.dismiss();
                                                                                        this.loading_data = false;
                                                                                        reject();
                                                                                    }
                                                                                })
                                                                                .catch((error) => {
                                                                                    console.error(error);
                                                                                    this.spinner.dismiss();
                                                                                    this.loading_data = false;
                                                                                    reject();
                                                                                });
                                                                            }
                                                                            else {
                                                                                this.spinner.dismiss();
                                                                                this.loading_data = false;
                                                                                reject();
                                                                            }

                                                                        })
                                                                        .catch((error) => {
                                                                            console.error(error);
                                                                            this.spinner.dismiss();
                                                                            this.loading_data = false;
                                                                            reject();
                                                                        });
                                                                    }
                                                                    else {
                                                                        this.spinner.dismiss();
                                                                        this.loading_data = false;
                                                                        reject();
                                                                    }
                                                                })
                                                                .catch((error) => {
                                                                    console.error(error);
                                                                    this.spinner.dismiss();
                                                                    this.loading_data = false;
                                                                    reject();
                                                                });
                                                            }
                                                            else {
                                                                this.spinner.dismiss();
                                                                this.loading_data = false;
                                                                reject();
                                                            }
                                                        })
                                                        .catch((error) => {
                                                            console.error(error);
                                                            this.spinner.dismiss();
                                                            this.loading_data = false;
                                                            reject();
                                                        });
                                                    }
                                                    else {
                                                        this.spinner.dismiss();
                                                        this.loading_data = false;
                                                        reject();
                                                    }
                                                })
                                                .catch((error) => {
                                                    console.error(error);
                                                    this.spinner.dismiss();
                                                    this.loading_data = false;
                                                    reject();
                                                });
                                            }
                                            else {
                                                this.spinner.dismiss();
                                                this.loading_data = false;
                                                reject();
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                            this.spinner.dismiss();
                                            this.loading_data = false;
                                            reject();
                                        });
                                    }
                                    else {
                                        this.spinner.dismiss();
                                        this.loading_data = false;
                                        reject();
                                    }
                                }
                                else {
                                    this.spinner.dismiss();
                                    this.loading_data = false;
                                    reject();
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                if(error) {
                                    if(error.status === 400) {
                                        this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to retrieve the data.");
                                    } else if(error.status === 403) {
                                        this.presentAlert("Insufficient permissions", "You don't have permission to retrieve the data.");
                                    } else if(error.status == 404) {
                                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve the data at this time.");
                                    }
                                    else if(error.status == 0) {
                                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                                    }
                                }
                                this.spinner.dismiss();
                                this.loading_data = false;
                                reject();
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            this.spinner.dismiss();
                            this.loading_data = false;
                            reject();
                        });
                    }
                    else {
                        this.spinner.dismiss();
                        this.loading_data = false;
                        reject();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.spinner.dismiss();
                    this.loading_data = false;
                    reject();
                });
            }
        });
        return promise;
    }

    initializePusher() {

        this.storageService.get("connected_account_id").then((connected_account_id) => {

            if (connected_account_id) {

                this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {

                    this.pusher_channel = this.pusherService.init(connected_account['site_name'], connected_account['server_name'], connected_account['access_token']);

                    this.smartpayReaderService.init();

                    this.pusher_channel.bind("user_added", (user) => {
                        this.userService.addUser(user);
                    });

                    this.pusher_channel.bind("user_deleted", (user) => {
                        this.userService.deleteUser(user);
                    });

                    this.pusher_channel.bind("user_updated", (user) => {
                        this.userService.updateUser(user);
                        if(this.user.id === user.id) {
                            if(!user.active) {
                                this.router.navigate(['disconnect-account'], { replaceUrl: true });
                            }
                        }
                    });

                    this.pusher_channel.bind("register_added", (register) => {
                        this.registerService.addRegister(register);
                    });

                    this.pusher_channel.bind("register_deleted", (register) => {
                        this.registerService.deleteRegister(register).then(() => {
                            this.storageService.get("register_id").then((register_id) => {
                                if (register_id) {
                                    if (register.id === register_id) {
                                        this.registerService.getRegisters().then((registers) => {
                                            if (registers) {
                                                this.storageService.set("register_id", registers[0].id).then(() => {
                                                    this.observerService.publish("register_id_changed", registers[0].id);
                                                })
                                                .catch((error) => {
                                                    console.error(error);
                                                });
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("register_updated", (register) => {
                        this.registerService.updateRegister(register);
                    });

                    this.pusher_channel.bind("pricing_tier_added", (pricing_tier) => {
                        this.pricingTierService.addPricingTier(pricing_tier);
                    });

                    this.pusher_channel.bind("pricing_tier_deleted", (pricing_tier) => {
                        this.pricingTierService.deletePricingTier(pricing_tier).then(() => {
                            this.pricingTierService.getPricingTier('FD5F3DDA-B23C-DF79-8676151A26F699B1').then((retail_pricing_tier) => {
                                this.registerService.getRegisters().then(async (registers: any) => {
                                    if (registers) {
                                        for (let i = 0; i < registers.length; i++) {
                                            if (pricing_tier.id === registers[i].default_pricing_tier.id) {
                                                registers[i].default_pricing_tier = retail_pricing_tier;
                                                await this.registerService.updateRegister(registers[i]);
                                            }
                                        }
                                    }
                                    this.cartService.get().then((cart) => {
                                        if (pricing_tier.id === cart["pricing_tier"].id) {
                                            this.cartService.reset();
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                    this.personService.updatePeoplePricingTier(pricing_tier.id, retail_pricing_tier['id']);
                                    this.organisationService.updateOrganisationsPricingTier(pricing_tier.id, retail_pricing_tier['id']);
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("pricing_tier_updated", (pricing_tier) => {
                        this.pricingTierService.updatePricingTier(pricing_tier).then(() => {
                            this.registerService.getRegisters().then(async (registers: any) => {
                                if (registers) {
                                    for (let i = 0; i < registers.length; i++) {
                                        if (pricing_tier.id === registers[i].default_pricing_tier.id) {
                                            registers[i].default_pricing_tier = pricing_tier;
                                            this.registerService.updateRegister(registers[i]);
                                        }
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                        // NOTE: we don't update pricing tier in cart as would cause unexpected pricing changes to real time transaction
                    });

                    this.pusher_channel.bind("tax_zone_added", (tax_zone) => {
                        this.taxZoneService.addTaxZone(tax_zone);
                    });

                    this.pusher_channel.bind("tax_zone_deleted", (tax_zone) => {
                        this.taxZoneService.deleteTaxZone(tax_zone).then(() => {
                            this.taxZoneService.getTaxZones().then((tax_zones: any) => {
                                tax_zones.sort((a, b) => {
                                    if (a['country']['title'].toLowerCase() < b['country']['title'].toLowerCase()) { return -1; }
                                    if (a['country']['title'].toLowerCase() > b['country']['title'].toLowerCase()) { return 1; }
                                    return 0;
                                });
                                let alternative_tax_zone = tax_zones[0];
                                this.registerService.getRegisters().then(async (registers: any) => {
                                    if (registers) {
                                        for (let i = 0; i < registers.length; i++) {
                                            if (tax_zone.id === registers[i].location.tax_zone.id) {
                                                registers[i].location.tax_zone = alternative_tax_zone;
                                                await this.registerService.updateRegister(registers[i]);
                                            }
                                        }
                                    }
                                    this.cartService.get().then((cart) => {
                                        if (tax_zone.id === cart["tax_zone"].id) {
                                            this.cartService.reset();
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("tax_zone_updated", (tax_zone) => {
                        this.taxZoneService.updateTaxZone(tax_zone).then(() => {
                            this.registerService.getRegisters().then(async (registers: any) => {
                                if (registers) {
                                    for (let i = 0; i < registers.length; i++) {
                                        if (tax_zone.id === registers[i].location.tax_zone.id) {
                                            registers[i].location.tax_zone = tax_zone;
                                            await this.registerService.updateRegister(registers[i]);
                                        }
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                            // NOTE: we don't update tax zone in cart as would cause unexpected pricing changes to real time transaction
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("supported_currency_added", (supported_currency) => {
                        if(supported_currency.pos_channel == true) {
                            this.supportedCurrencyService.addSupportedCurrency(supported_currency);
                        }
                    });

                    this.pusher_channel.bind("supported_currency_deleted", (supported_currency) => {
                        this.supportedCurrencyService.deleteSupportedCurrency(supported_currency).then(() => {
                            this.supportedCurrencyService.getBaseSupportedCurrency().then((base_currency) => {
                                if(base_currency) {
                                    this.registerService.getRegisters().then(async (registers: any) => {
                                        if(registers) {
                                            for(let i = 0; i < registers.length; i++) {
                                                if(supported_currency.id === registers[i].location.default_currency.id) {
                                                    registers[i].location.default_currency = base_currency;
                                                    await this.registerService.updateRegister(registers[i]);
                                                }
                                            }
                                        }
                                        this.cartService.get().then((cart) => {
                                            if (supported_currency.id === cart["currency"].id) {
                                                this.cartService.reset();
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("supported_currency_updated", (supported_currency) => {
                        if(supported_currency.pos_channel == true) {
                            this.supportedCurrencyService.updateSupportedCurrency(supported_currency).then(() => {
                                this.registerService.getRegisters().then((registers: any) => {
                                    if(registers) {
                                        for (let i = 0; i < registers.length; i++) {
                                            if (registers[i]['location'].default_currency.id == supported_currency.id) {
                                                registers[i]['location'].default_currency = supported_currency;
                                                this.registerService.updateRegister(registers[i]);
                                            }
                                        }
                                    }
                                    this.cartService.get().then((cart) => {
                                        if(cart && cart['currency'] && supported_currency.id === cart['currency'].id) {
                                            this.cartService.validateCurrency().then().catch(() => { });
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        }
                        else {
                            this.supportedCurrencyService.deleteSupportedCurrency(supported_currency).then(() => {
                                this.supportedCurrencyService.getBaseSupportedCurrency().then((base_currency) => {
                                    if(base_currency) {
                                        this.registerService.getRegisters().then(async (registers: any) => {
                                            if(registers) {
                                                for(let i = 0; i < registers.length; i++) {
                                                    if(supported_currency.id === registers[i].location.default_currency.id) {
                                                        registers[i].location.default_currency = base_currency;
                                                        await this.registerService.updateRegister(registers[i]);
                                                    }
                                                }
                                            }
                                            this.cartService.get().then((cart) => {
                                                if (supported_currency.id === cart["currency"].id) {
                                                    this.cartService.reset();
                                                }
                                            })
                                            .catch((error) => {
                                                console.error(error);
                                            });
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    }
                                })
                                .catch((error) => {
                                    console.error(error);
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        }
                    });

                    this.pusher_channel.bind("person_added", (person) => {
                        if(person.archived == false) {
                            this.personService.addPerson(person).then(() => { }).catch(() => {
                                // soft fail, e.g. person was added from the app directly
                            });
                        }
                    });

                    this.pusher_channel.bind("person_deleted", (person) => {
                        this.personService.deletePerson(person).then(() => {
                            this.cartService.get().then((cart) => {
                                if (cart['person']) {
                                    if (cart['person'].id === person.id) {
                                        this.cartService.removePerson();
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    // TODO: tidy this function up after testing
                    this.pusher_channel.bind("person_updated", (person) => {

                        // get person first
                        this.personService.getPerson(person.id).then((existing_person: any) => {

                            if(existing_person) {

                                // if person exists and person.archived == false, update person
                                if(person.archived == false) {
                                    this.personService.updatePerson(person).then(() => {
                                        this.cartService.get().then((cart) => {
                                            if(cart['person']) {
                                                if(cart['person'].id == person.id) {
                                                    this.cartService.updatePerson(person['id']).then(() => {
                                                        if(!person['organisation']) {
                                                            this.cartService.removeOrganisation();
                                                        }
                                                    });
                                                }
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }
                                // if person exists and person.archived == true, delete person
                                else if(person.archived == true) {
                                    this.personService.deletePerson(person).then(() => {
                                        this.cartService.get().then((cart) => {
                                            if (cart['person']) {
                                                if (cart['person'].id === person.id) {
                                                    this.cartService.removePerson();
                                                }
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }

                            }
                            else {

                                // if person dosent exist and person.archived == false, add person
                                if(person.archived == false) {
                                    this.personService.addPerson(person).catch(() => {});
                                }

                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    });

                    this.pusher_channel.bind("people_added", () => {
                        this.presentAlert("People added", "Some new people have been added by an import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    this.pusher_channel.bind("people_updated", () => {
                        this.presentAlert("People updated", "Some of your people have been updated by a bulk update or import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    // NOTE: We do a complete reload because things are too complex to shift around, easier to get 'tree' again
                    this.pusher_channel.bind("product_category_added", (category) => {
                        this.productService.loadProductCategories();
                    });

                    this.pusher_channel.bind("product_category_deleted", (category) => {
                        this.productService.loadProductCategories();
                    });

                    this.pusher_channel.bind("product_category_updated", (category) => {
                        this.productService.loadProductCategories();
                    });

                    this.pusher_channel.bind("product_category_moved", () => {
                        this.productService.loadProductCategories();
                    });

                    this.pusher_channel.bind("product_added", (product) => {
                        if(product.pos_channel == true) {
                            this.productService.addProduct(product);
                        }
                    });

                    this.pusher_channel.bind("product_deleted", (product) => {
                        this.productService.deleteProduct(product.id).then(() => {
                            this.cartService.deleteItem(product.id);
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("product_updated", (product) => {

                        this.productService.getProduct(product.id).then((existing_product: any) => {

                            if(existing_product) {

                                // if exists and archived == false or pos channel == false then delete
                                if(product.archived == true || product.pos_channel == false) {
                                    this.productService.deleteProduct(product.id).then(() => {
                                        if(product.allow_variants == true) {
                                            this.productService.getProductVariants(product.id).then((variants: any) => {
                                                for(let i = 0; i < variants.length; i++) {
                                                    this.productService.deleteProductVariant(variants[i]);
                                                }
                                            })
                                            .catch(() => {});
                                        }
                                    })
                                    .catch(() => {});
                                }

                                // if exists and archived == false or pos_channel == true then update
                                else if(product.pos_channel == true) {
                                    this.productService.updateProduct(product).catch(() => {});
                                }

                            }
                            else {

                                // if doesn't exist and archived == false and pos_channel == true then add
                                if(product.pos_channel == true && product.archived != true) {
                                    this.productService.addProduct(product).then(() => {
                                        if(product.allow_variants == true) {
                                            this.apiService.getProductVariants(product.id, {archived: false, pos_channel: true, include_suppliers: true, fields: 'id,product.id,product.image,track_stock,tax_type,quantity_decimal_places,default_pos_quantity,pos_quantity_step_interval,available_stock,low_available_stock_level,prices,sku,barcode,manufacturer_barcode,options,options.value,measurement_unit.id'}).then((variants) => {
                                                for(let i = 0; i < variants.length; i++) {
                                                    this.productService.addProductVariant(variants[i]);
                                                }
                                            })
                                            .catch(() => {});
                                        }
                                    })
                                    .catch(() => {});
                                }

                            }

                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    });

                    this.pusher_channel.bind("product_available_stock_updated", (product) => {
                        this.productService.updateProductAvailableStock(product);
                    });

                    this.pusher_channel.bind("products_added", () => {
                        this.presentAlert("Products added", "Some new products have been added by an import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    this.pusher_channel.bind("products_updated", () => {
                        this.presentAlert("Products updated", "Some of your products have been updated by a bulk update or import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    this.pusher_channel.bind("product_variant_added", (product_variant) => {
                        if(product_variant.pos_channel == true) {
                            this.productService.addProductVariant(product_variant);
                        }
                    });

                    this.pusher_channel.bind("product_variant_deleted", (product_variant) => {
                        this.productService.deleteProductVariant(product_variant).then(() => {
                            this.cartService.deleteItem(product_variant.id);
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    this.pusher_channel.bind("product_variant_updated", (product_variant) => {

                        this.productService.getProductVariant(product_variant.id).then((existing_product_variant: any) => {

                            if(existing_product_variant) {

                                // if exists and archived == false or pos channel == false then delete
                                if(product_variant.archived == true || product_variant.pos_channel == false) {
                                    this.productService.deleteProductVariant(product_variant).then(() => {}).catch(() => {});
                                }

                                // if exists and archived == false or pos_channel == true then update
                                else if(product_variant.pos_channel == true) {
                                    this.productService.updateProductVariant(product_variant).catch(() => {});
                                }

                            }
                            else {

                                // if doesn't exist and archived == false and pos_channel == true then add
                                if(product_variant.pos_channel == true && product_variant.archived != true) {
                                    this.productService.addProductVariant(product_variant);
                                }

                            }

                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    });

                    this.pusher_channel.bind("product_variant_available_stock_updated", (product_variant) => {
                        this.productService.updateProductVariantAvailableStock(product_variant);
                    });

                    this.pusher_channel.bind("event_category_added", (category) => {
                        this.eventService.loadEventCategories();
                    });

                    this.pusher_channel.bind("event_category_deleted", (category) => {
                        this.eventService.loadEventCategories();
                    });

                    this.pusher_channel.bind("event_category_updated", (category) => {
                        this.eventService.loadEventCategories();
                    });

                    this.pusher_channel.bind("event_category_moved", () => {
                        this.eventService.loadEventCategories();
                    });

                    this.pusher_channel.bind("event_added", (event) => {
                        this.eventService.addEvent(event);
                    });

                    this.pusher_channel.bind("event_deleted", (event) => {
                        this.eventService.getEventTicketTypes(event.id).then(async (ticket_types: any) => {
                            if (ticket_types) {
                                for (let i = 0; i < ticket_types.length; i++) {
                                    await this.cartService.deleteItem(ticket_types[i].id);
                                    await this.eventService.deleteTicketType(ticket_types[i].id);
                                }
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                        this.eventService.getEventSessions(event.id).then((event_sessions: any) => {
                            for (let i = 0; i < event_sessions.length; i++) {
                                this.eventService.deleteEventSession(event_sessions[i]);
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                        this.eventService.deleteEvent(event.id);
                    });

                    this.pusher_channel.bind("event_updated", (event) => {
                        this.eventService.updateEvent(event).catch(() => {
                            // soft fail, event doesn't exist on device because it's not current anymore
                        });
                    });

                    this.pusher_channel.bind("event_session_added", (event_session) => {
                        this.eventService.addEventSession(event_session);
                    });

                    this.pusher_channel.bind("event_session_deleted", (event_session) => {
                        this.eventService.getEventSessionTicketTypes(event_session.id).then(async (ticket_types: any) => {
                            if (ticket_types) {
                                for (let i = 0; i < ticket_types.length; i++) {
                                    await this.cartService.deleteItem(ticket_types[i].id);
                                }
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                        this.eventService.deleteEventSession(event_session);
                    });

                    this.pusher_channel.bind("event_session_updated", (event_session) => {
                        this.eventService.updateEventSession(event_session).catch(() => {
                            // soft fail, doesn't exist because no longer current
                        });
                    });

                    this.pusher_channel.bind("ticket_type_added", (ticket_type) => {
                        this.eventService.addTicketType(ticket_type);
                    });

                    this.pusher_channel.bind("ticket_type_deleted", (ticket_type) => {
                        if (ticket_type) {
                            this.cartService.deleteItem(ticket_type.id);
                        }
                        this.eventService.deleteTicketType(ticket_type);
                    });

                    this.pusher_channel.bind("ticket_type_updated", (ticket_type) => {
                        this.eventService.updateTicketType(ticket_type).catch(() => {
                            // soft fail, doesn't exist because no longer current
                        });
                    });

                    this.pusher_channel.bind("organisation_added", (organisation) => {
                        if(organisation.archived == false) {
                            this.organisationService.addOrganisation(organisation).catch(() => {
                                // soft fail, e.g. added from the app directly
                            });
                        }
                    });

                    this.pusher_channel.bind("organisation_deleted", (organisation) => {
                        this.organisationService.deleteOrganisation(organisation).then(() => {
                            this.cartService.get().then((cart) => {
                                if (cart['organisation']) {
                                    if (cart['organisation'].id === organisation.id) {
                                        this.cartService.removeOrganisation();
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                    });

                    // TODO: tidy this function up after testing
                    this.pusher_channel.bind("organisation_updated", (organisation) => {

                        // get organisation first
                        this.organisationService.getOrganisation(organisation.id, {include_contract_prices: false}).then((existing_organisation: any) => {

                            if(existing_organisation) {

                                // if organisation exists and organisation.archived == false, update organisation
                                if(organisation.archived == false) {
                                    this.organisationService.updateOrganisation(organisation).then(() => {
                                        this.cartService.get().then((cart) => {
                                            if (cart['organisation']) {
                                                if (cart['organisation'].id === organisation.id) {
                                                    this.cartService.updateOrganisation(organisation.id).catch(() => {});
                                                }
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }
                                // if organisation exists and organisation.archived == true, delete organisation
                                else if(organisation.archived == true) {
                                    this.organisationService.deleteOrganisation(organisation).then(() => {
                                        this.cartService.get().then((cart) => {
                                            if (cart['organisation']) {
                                                if (cart['organisation'].id === organisation.id) {
                                                    this.cartService.removeOrganisation();
                                                }
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error);
                                        });
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                    });
                                }

                            }
                            else {

                                // if organisation dosent exist and organisation.archived == false, add organisation
                                if(organisation.archived == false) {
                                    this.organisationService.addOrganisation(organisation).catch(() => {});
                                }

                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    });

                    this.pusher_channel.bind("organisations_added", () => {
                        this.presentAlert("Organisations added", "Some new organisations have been added by an import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    this.pusher_channel.bind("organisations_updated", () => {
                        this.presentAlert("Organisations updated", "Some of your organisations have been updated by a bulk update or import. <br><br>Please click <strong>Reload data</strong> in the menu, when you can, to ensure you have the latest data.");
                    });

                    /*
                    TODO: deleteLocation (get [0] as default), updateLocation
                    TODO: updateUser
                    */

                    this.pusher_channel.bind("order_added", (order) => {
                        this.observerService.publish("order_added", order);
                    });

                    this.pusher_channel.bind("order_deleted", (order) => {
                        this.observerService.publish("order_deleted", order);
                    });

                    this.pusher_channel.bind("order_updated", (order) => {
                        this.observerService.publish("order_updated", order);
                    });

                    this.pusher_channel.bind("order_item_updated", (item) => {
                        this.observerService.publish("order_item_updated", item);
                    });

                    this.pusher_channel.bind("order_payment_updated", (payment) => {
                        this.observerService.publish("order_payment_updated", payment);
                    });

                    this.pusher_channel.bind("order_refund_updated", (refund) => {
                        this.observerService.publish("order_refund_updated", refund);
                    });

                    this.pusher_channel.bind("order_return_updated", (order_return) => {
                        this.observerService.publish("order_return_updated", order_return);
                    });

                    this.pusher_channel.bind("order_fulfillment_updated", (fulfillment) => {
                        this.observerService.publish("order_fulfillment_updated", fulfillment);
                    });

                    this.pusher_channel.bind("parked_cart_added", (parked_cart) => {
                        this.observerService.publish("parked_cart_added", parked_cart);
                    });

                    this.pusher_channel.bind("parked_cart_deleted", (parked_cart) => {
                        this.observerService.publish("parked_cart_deleted", parked_cart['id']);
                    });

                    this.pusher_channel.bind("parked_cart_updated", (parked_cart) => {
                        this.observerService.publish("parked_cart_updated", parked_cart);
                    });

                })
                .catch((error) => {
                    console.error(error);
                });
            }
        })
        .catch((error) => {
            console.error(error);
        });
    }

    menuClose() {
        this.menu.close();
    }

    menuWillOpen() {
        this.pendingOrderService.getPendingOrders().then((pending_orders: []) => {
            this.pending_order_count = pending_orders.length;
        })
        .catch((error) => {
            console.error(error);
        });
    }

    processPendingOrders() {
        if(this.online){
            this.menu.close();
            this.pendingOrderService.getPendingOrders().then((pending_orders: []) => {
                if(pending_orders.length) {
                    this.presentSpinner("Processing pending orders").then(() => {
                        this.pendingOrderService.processPendingOrders().then(() => {
                            this.spinner.dismiss();
                        })
                        .catch((error) => {
                            this.spinner.dismiss();
                        });
                    });
                }
                else {
                    this.presentAlert("No pending orders", "There are no pending orders to process.");
                }
            })
            .catch((error) => {
                console.error(error);
            });
        }
        else {
            this.presentAlert("Offline", "You must be online to process pending orders, please check your network connection and try again.");
        }
    }

    reloadData() {
        this.storageService.remove('registers_last_updated_date');
        this.storageService.remove('supported_currencies_last_updated_date');
        this.storageService.remove('tax_zones_last_updated_date');
        this.storageService.remove('pricing_tiers_last_updated_date');
        this.storageService.remove('product_categories_last_updated_date');
        this.storageService.remove('event_categories_last_updated_date');
        this.storageService.remove('person_email_lists_last_updated_date');
        this.storageService.remove('countries_last_updated_date');
        this.storageService.remove('couriers_last_updated_date');
        this.storageService.remove('products_last_updated_date');
        this.storageService.remove('measurement_units_last_updated_date');
        this.storageService.remove('users_last_updated_date');
        this.storageService.remove('events_last_updated_date');
        this.storageService.remove('people_last_updated_date');
        this.storageService.remove('organisations_last_updated_date');
        this.storageService.remove('custom_payment_services_last_updated_date');
        this.loadData().then(() => {}).catch((error) => {});
        this.menu.close();
    }

    async openWebsiteURL(website_url) {
        website_url = encodeURI(website_url);
        if(Capacitor.isNativePlatform()) {
            await Browser.open({ url: website_url });
        } else {
            window.open(website_url, "_blank", "toolbar=no,transitionstyle=fliphorizontal,location=no");
        }
    }

    async presentChangePINModal() {
        this.menu.close();
        const modal = await this.modalController.create({
            component: UserPINChangePage,
            cssClass: 'user-pin-change',
            componentProps: { user: this.user }
        });
        return await modal.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

    async presentNewVersionAvailableAlert(header, message) {
        this.new_version_available_alerted = true;
        this.new_version_available_alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await this.new_version_available_alert.present();
        // NOTE: Ionic focuses the only button on present and its focus-visible util starts in keyboard mode (until first mouse/touch), so remove the focused highlight
        this.new_version_available_alert.querySelector('.alert-button')?.classList.remove('ion-focused');
    }

    async presentUpdateAvailableAlert() {
        this.update_available_alerted = true;
        const alert = await this.alertController.create({
            header: 'Update available',
            message: "A new version of the POS is available, please update when you get a chance.",
            buttons: [
                {
                    text: 'Later',
                    role: 'cancel'
                },
                {
                    text: 'Update',
                    handler: () => {
                        this.openAppStore();
                    }
                }
            ]
        });
        await alert.present();
    }

    async presentUpdateRequiredAlert() {
        this.update_required_alerted = true;
        const alert = await this.alertController.create({
            header: 'Update required',
            message: "This version of the POS is no longer supported, please update to continue.",
            backdropDismiss: false,
            buttons: [
                {
                    text: 'Update',
                    handler: () => {
                        this.openAppStore();
                        // NOTE: keep the alert open so the POS can't be used until updated
                        return false;
                    }
                }
            ]
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: true
        });
        await this.spinner.present();
    }

    goToQuotes(quotes_url) {
        if(this.online){
            this.menu.close();
            this.router.navigateByUrl(quotes_url);
        }
        else {
            this.presentAlert("Offline", "You must be online to view quotes, please check your network connection and try again.");
        }
    }

    goToParkedCarts(parked_carts_url) {
        if(this.online){
            this.menu.close();
            this.router.navigateByUrl(parked_carts_url);
        }
        else {
            this.presentAlert("Offline", "You must be online to view parked carts, please check your network connection and try again.");
        }
    }

    goToOrders(orders_url) {
        if(this.online){
            this.menu.close();
            this.router.navigateByUrl(orders_url);
        }
        else {
            this.presentAlert("Offline", "You must be online to view orders, please check your network connection and try again.");
        }
    }

    goToPeople(people_url) {
        if(this.online){
            this.menu.close();
            this.router.navigateByUrl(people_url);
        }
        else {
            this.presentAlert("Offline", "You must be online to view people, please check your network connection and try again.");
        }
    }

    goToOrganisations(organisations_url) {
        if(this.online){
            this.menu.close();
            this.router.navigateByUrl(organisations_url);
        }
        else {
            this.presentAlert("Offline", "You must be online to view organisations, please check your network connection and try again.");
        }
    }

}
